<template>
  <div>
    <b-modal id="serial-number-step-1" size="lg" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <h2 class="modal-title"><i class="fas fa-plus colorOrange mr-1" /> {{ $t('xAddAGen') }}</h2>
      </template>
      <template #modal-footer>
        <b-button
          variant="outline-warning"
          @click="closeEnrollmentModals('sn-verification')"
          data-test-hook="cancel-button-step1-enrollment"
        >
          {{ $t('xCancel') }}
        </b-button>
        <b-button
          variant="primary"
          @click="validateSerialNumber()"
          :disabled="validatingSN"
          data-test-hook="ContinueBtn-step1-enrollment"
        >
          {{ $t('xContinue') }} <i class="fas fa-arrow-right fa-lg" />
          <b-spinner class="ml-2" v-if="validatingSN"></b-spinner>
        </b-button>
      </template>
      <b-row align-v="center" class="mb-2">
        <b-col>
          <h5>
            <strong>{{ $t('xEnterYourGenSN') }}</strong>
          </h5>
        </b-col>
        <b-col cols="auto" class="float-right text-right">
          <i class="fas fa-info-circle fa-lg colorOrange hoverPointer" v-b-modal.generator-serial-number-help />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-input-group>
            <b-form-input
              maxlength="30"
              v-model="userEnteredSerialNumber"
              autocomplete="new-password"
              data-test-hook="SerialNumberField"
            />
          </b-input-group>
        </b-col>
      </b-row>
      <div
        v-if="showSerialNumberValidationError && serialNumberValidationMessage"
        :show="showSerialNumberValidationError"
        class="alert alert-danger alert--validation"
        data-test-hook="SerialNumberInvalidText"
      >
        {{ serialNumberValidationMessage }}
      </div>
      <b-row class="mt-5" align-v="center">
        <b-col class="mr-0 pr-0">
          <b-progress :value="16.67" variant="primary"></b-progress>
        </b-col>
        <b-col cols="auto" class="ml-0 pl-0">
          <i class="fas fa-shield-check fa-lg inProgress ml-1 pl-0" />
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="generator-serial-number-help"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      hide-footer
      centered
    >
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pl-0">
            <b-button variant="icon-button" v-b-modal.serial-number-step-1 data-test-hook="x-button-snHelp-enrollment">
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2 class="modal-title">
              {{ $t('xSNHelp') }}
            </h2>
          </b-col>
        </b-row>
      </template>
      <b-row class="my-3">
        <b-col class="d-flex justify-content-center">
          <b-img
            class="sn-tag-img"
            alt="Serial Number Illustration"
            fluid
            :src="require('@/assets/images/serial-number-tag.svg')"
          />
        </b-col>
      </b-row>
      <b-row class="mt-5">
        <b-col>
          <p>
            <strong>{{ $t('xLocateTheGeneratorSerialNumber') }}</strong>
          </p>
          <p>{{ $t('x1BehindTheFrontPanelOfYourGenerator') }}</p>
          <p>{{ $t('x2OnTheBackOfTheBoxOfYourGeneratorPackaging') }}</p>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal id="gen-name-step-2" size="lg" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pr-0">
            <b-button
              variant="icon-button"
              v-b-modal.serial-number-step-1
              data-test-hook="back-button-step2-enrollment"
            >
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2 class="modal-title"><i class="fas fa-plus colorOrange mr-1" /> {{ $t('xAddAGen') }}</h2>
          </b-col>
        </b-row>
      </template>
      <template #modal-footer>
        <b-button
          variant="outline-warning"
          @click="closeEnrollmentModals('set-apparatus-name')"
          data-test-hook="cancel-button-step2-enrollment"
        >
          {{ $t('xCancel') }}
        </b-button>
        <b-button
          variant="primary"
          v-b-modal.generator-fuel-type-step-3
          :disabled="!generatorName"
          data-test-hook="ContinueBtn-step2-enrollment"
        >
          {{ $t('xContinue') }} <i class="fas fa-arrow-right" />
        </b-button>
      </template>
      <b-row>
        <b-col>
          <h5>
            <strong>{{ $t('xNameYourGen') }}</strong>
          </h5>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-input-group>
            <b-form-input
              maxlength="50"
              v-model="generatorName"
              autocomplete="new-password"
              data-test-hook="GeneratorNameField"
            />
          </b-input-group>
        </b-col>
      </b-row>
      <b-row class="mt-5" align-v="center">
        <b-col class="mr-0 pr-0">
          <b-progress :value="33.33" variant="primary"></b-progress>
        </b-col>
        <b-col cols="auto" class="ml-0 pl-0">
          <i class="fas fa-shield-check fa-lg inProgress ml-1 pl-0" />
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="generator-fuel-type-step-3"
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      centered
    >
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pr-0">
            <b-button
              variant="icon-button"
              v-b-modal.v-b-modal.gen-name-step-2
              data-test-hook="back-button-step3-enrollment"
            >
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2 class="modal-title"><i class="fas fa-plus colorOrange mr-1" /> {{ $t('xSelectFuelType') }}</h2>
          </b-col>
        </b-row>
      </template>
      <template #modal-footer>
        <b-button
          variant="outline-warning"
          @click="closeEnrollmentModals('add-apparatus-fuel-type')"
          data-test-hook="cancel-button-step3-enrollment"
        >
          {{ $t('xCancel') }}
        </b-button>
        <b-button
          variant="primary"
          v-b-modal.generator-address-step-4
          data-test-hook="ContinueBtn-step3-enrollment"
          @click="setFuelType()"
          :disabled="selectedFuelType == null"
        >
          {{ $t('xContinue') }} <i class="fas fa-arrow-right" />
        </b-button>
      </template>
      <b-row>
        <b-col>
          <h5>
            <strong>{{ $t('xFuelType') }}</strong>
          </h5>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-select v-model="selectedFuelType" :options="fuelOptions">
            <template #first>
              <b-form-select-option :value="null" disabled>{{ $t('xPleaseSelectYourFuelType') }}</b-form-select-option>
            </template>
          </b-form-select>
        </b-col>
      </b-row>
      <b-row class="mt-5" align-v="center">
        <b-col class="mr-0 pr-0">
          <b-progress :value="50.0" variant="primary"></b-progress>
        </b-col>
        <b-col cols="auto" class="ml-0 pl-0">
          <i class="fas fa-shield-check fa-lg inProgress ml-1 pl-0" />
        </b-col>
      </b-row>
    </b-modal>

    <b-modal id="generator-address-step-4" size="lg" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pr-0">
            <b-button
              variant="icon-button"
              v-b-modal.v-b-modal.generator-fuel-type-step-3
              data-test-hook="back-button-step4-enrollment"
            >
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2 class="modal-title"><i class="fas fa-plus colorOrange mr-1" /> {{ $t('xAddAGen') }}</h2>
          </b-col>
        </b-row>
      </template>
      <template #modal-footer>
        <!-- Normal footer buttons -->
        <b-row class="box" v-if="!showValidationView">
          <b-button
            variant="outline-warning"
            @click="closeEnrollmentModals('add-apparatus-address')"
            data-test-hook="cancel-button-step4-enrollment"
          >
            {{ $t('xCancel') }}
          </b-button>
          <b-button
            variant="primary"
            @click="addGeneratorAddress()"
            data-test-hook="ContinueBtn-step4-enrollment"
            :disabled="continueButtonDisabled"
          >
            {{ $t('xContinue') }} <i class="fas fa-arrow-right" />
          </b-button>
        </b-row>
        <!-- Validation footer buttons -->
        <b-row class="box" v-else-if="showValidationView && !showContactSupportButton">
          <b-button variant="secondary" @click="editAddress" data-test-hook="edit-address-button">
            {{ $t('xAddressSearchEditAddress') }}
          </b-button>
          <b-button
            variant="warning"
            @click="confirmAddress"
            style="justify-content: center;"
            :disabled="confirmAddressDisabled"
            data-test-hook="confirm-address-button"
          >
            {{ $t('xAddressSearchConfirmAddress') }}
            <span v-if="saving">
              <b-spinner small />
            </span>
            <span v-else>
              <i class="far fa-arrow-right"></i>
            </span>
          </b-button>
        </b-row>
        <!-- Contact Support footer buttons -->
        <b-row class="box" v-else-if="showValidationView && showContactSupportButton">
          <b-button variant="secondary" @click="editAddress" data-test-hook="edit-address-button">
            {{ $t('xAddressSearchEditAddress') }}
          </b-button>
          <b-button
            variant="warning"
            @click="$router.push({ path: `/support` })"
            style="justify-content: center;"
            data-test-hook="contact-support-button"
          >
            {{ $t('xContactSupport') }}
          </b-button>
        </b-row>
      </template>
      <div v-if="!showValidationView">
        <b-row>
          <b-col>
            <h5>
              <strong>{{ $t('xSelectYourGenAddress') }}</strong>
            </h5>
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col>
            <b-form-checkbox
              class="font-weight-bold"
              v-model="useAccountAddress"
              data-test-hook="use-acct-address-checkbox-step4-enrollment"
            >
              {{ $t('xUseAcctAddress') }}
            </b-form-checkbox>
          </b-col>
        </b-row>
      </div>
      <b-row v-if="useAccountAddress" data-test-hook="GeneratorAddress">
        <b-col>
          <p>{{ DisplayAddress(accountProfile.address) }}</p>
        </b-col>
      </b-row>
      <b-row v-else>
        <b-col>
          <AddressSearchModals
            ref="genEnrollmentAddressSearchModalsRef"
            addressSearchModalId="gen-enrollment-address-search-form"
            addressValidationModalId="gen-enrollment-address-validation-form"
            @onSubmit="updateGeneratorAddress"
            @addressSearchModalViewChanged="handleValidationStateChange"
            @setIsNextDisabled="handleSetIsNextDisabled"
            :addressFormTitle="$t('xEditInformation')"
            :inline="true"
            :showContinueButton="false"
            :showValidationButtons="false"
            :showAddressCheckmarkHeader="false"
          />
        </b-col>
      </b-row>
      <b-row class="mt-5" align-v="center">
        <b-col class="mr-0 pr-0">
          <b-progress :value="66.67" variant="primary"></b-progress>
        </b-col>
        <b-col cols="auto" class="ml-0 pl-0">
          <i class="fas fa-shield-check fa-lg inProgress ml-1 pl-0" />
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="confirm-gen-enrollment-step-5"
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      hide-header
      centered
    >
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pr-0">
            <b-button
              variant="icon-button"
              v-b-modal.generator-address-step-4
              data-test-hook="back-button-step5-enrollment"
            >
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2 class="modal-title"><i class="fas fa-plus colorOrange mr-1" /> {{ $t('xConfirmEnrollment') }}</h2>
          </b-col>
        </b-row>
      </template>
      <template #modal-footer>
        <b-button
          variant="outline-warning"
          @click="closeEnrollmentModals('confirm-apparatus')"
          data-test-hook="cancel-button-step5-enrollment"
        >
          {{ $t('xCancel') }}
        </b-button>
        <b-button
          variant="primary"
          @click="enrollApparatus()"
          :disabled="enrollingApparatus"
          data-test-hook="EnrollBtn-step5-enrollment"
        >
          {{ $t('xEnrollThisGenerator') }} <i class="fas fa-arrow-right" />
          <b-spinner class="ml-2" v-if="enrollingApparatus"></b-spinner>
        </b-button>
      </template>
      <b-row class="text-center">
        <b-col>
          <h2>
            {{ $t('xYourGenInfo') }}
          </h2>

          <h5>{{ generatorName }}</h5>
          <p v-if="useAccountAddress">{{ DisplayAddress(accountProfile.address) }}</p>

          <p v-else>{{ DisplayAddress(generatorAddress) }}</p>
          <p>{{ $t('xFuelType') }}: {{ selectedFuelTypeName }}</p>
        </b-col>
      </b-row>
      <div
        v-if="showEnrollApparatusError"
        :show="showEnrollApparatusError"
        class="alert alert-danger alert--validation"
      >
        {{ $t('xFailedToAddApparatus') }}
      </div>
      <b-row class="mt-5" align-v="center">
        <b-col class="mr-0 pr-0">
          <b-progress :value="83.33" variant="primary"></b-progress>
        </b-col>
        <b-col cols="auto" class="ml-0 pl-0">
          <i class="fas fa-shield-check fa-lg inProgress ml-1 pl-0" />
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="generator-enrollment-success"
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      hide-header
      centered
    >
      <template #modal-footer>
        <b-button
          variant="primary"
          @click="
            deviceType && (macAddress || searchLabel || shortDeviceId)
              ? $bvModal.show('confirm-existing-device')
              : $bvModal.show('select-device-type-step-6')
          "
          data-test-hook="AddCommBtn"
        >
          {{ 'Enroll a communication device' }} <i class="fas fa-arrow-right" />
        </b-button>
      </template>
      <b-row class="text-center mb-3">
        <b-col>
          <h1>{{ $t('xSuccess') }}!</h1>
          <h5>{{ $t('xYouHaveSuccessfullyEnrolledYourGenerator') }}</h5>
        </b-col>
      </b-row>
      <b-row class="text-center">
        <b-col>
          <b-img
            class="generator-success-img"
            alt="Generator Illustration"
            fluid
            :src="require('@/assets/images/gen-enrollment-success.svg')"
          />
        </b-col>
      </b-row>
      <div v-if="showRegistrationFailedError" class="alert alert--validation alert-danger">
        {{ $t('xYourGeneratorWasSuccessfullyEnrolledBut') }}
      </div>
    </b-modal>

    <b-modal id="confirm-existing-device" size="lg" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <h2 class="modal-title"><i class="fas fa-plus colorOrange mr-1" /> {{ $t('xConfirmCommunicationDevice') }}</h2>
      </template>
      <template #modal-footer>
        <b-button
          variant="outline-warning"
          data-test-hook="change-device-button"
          @click="$bvModal.show('select-device-type-step-6')"
        >
          {{ $t('xChangeDevice') }}
        </b-button>
        <b-button
          variant="primary"
          data-test-hook="confirm-device-button"
          @click="$bvModal.show('device-enrollment-success-step-9')"
        >
          {{ $t('xConfirm') }}
        </b-button>
      </template>
      <b-row>
        <b-col data-test-hook="existing-device-text">
          {{ 'xOurRecordsShowExistingDevice' | formatLabel }}
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col cols="12">
          <i class="asterisk-text" data-test-hook="device-type-label">{{ 'xDeviceType' | formatLabel }}</i>
        </b-col>
        <b-col cols="12">
          <strong data-test-hook="device-type">{{ deviceType }}</strong>
        </b-col>
      </b-row>
      <b-row class="mt-3" v-if="deviceType == 'wifi'">
        <b-col cols="12">
          <i class="asterisk-text" data-test-hook="device-serial-number-label">{{ 'xMacAddress' | formatLabel }}</i>
        </b-col>
        <b-col cols="12">
          <strong data-test-hook="device-serial-number">{{ macAddress }}</strong>
        </b-col>
      </b-row>
      <b-row v-else>
        <b-col cols="12">
          <i class="asterisk-text" data-test-hook="device-serial-number-label">{{
            'xDeviceSerialNumber' | formatLabel
          }}</i>
        </b-col>
        <b-col cols="12">
          <strong v-if="deviceType == hsbG3DeviceType" data-test-hook="device-serial-number">{{
            shortDeviceId
          }}</strong>
          <strong v-else data-test-hook="device-serial-number">{{ searchLabel }}</strong>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal id="select-device-type-step-6" size="lg" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <h2 class="modal-title"><i class="fas fa-plus colorOrange mr-1" /> {{ $t('xAddCommunicationDevice') }}</h2>
      </template>
      <template #modal-footer>
        <b-button
          variant="outline-warning"
          @click="closeEnrollmentModals('select-apparatus-device-type')"
          data-test-hook="cancel-button-step6-enrollment"
        >
          {{ $t('xCancel') }}
        </b-button>
        <b-button
          variant="primary"
          @click="enterDeviceInformation()"
          :disabled="!deviceType"
          data-test-hook="ConfirmBtn-step6-enrollment"
        >
          {{ $t('xConfirm') }} <i class="fas fa-arrow-right" />
        </b-button>
      </template>
      <b-row>
        <b-col>
          <b-card
            class="mb-3 hoverPointer"
            :class="selectedDeviceType('wifi')"
            @click="setDeviceType('wifi')"
            data-test-hook="wifi-clickable-card-step6-enrollment"
          >
            <b-card-body>
              <b-row align-v="center">
                <b-col cols="2" class="text-center">
                  <b-img
                    class="device-type-img"
                    :alt="$t('xOnBoardWiFi') + ' Illustration'"
                    :src="require('@/assets/images/onboard-wifi.svg')"
                  />
                </b-col>
                <b-col data-test-hook="WiFiBtn">
                  <strong> {{ $t('xOnBoardWiFi') }}</strong>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row v-if="g3DeviceFeatureFlagEnabled(currentAppConfig)">
        <b-col>
          <b-card
            class="mb-3 hoverPointer"
            :class="selectedDeviceType(hsbG3DeviceType)"
            @click="setDeviceType(hsbG3DeviceType)"
            data-test-hook="g3-clickable-card-step6-enrollment"
          >
            <b-card-body>
              <b-row align-v="center">
                <b-col cols="2" class="text-center">
                  <b-img
                    class="device-type-img"
                    :alt="$t('xConnectivityAccessoryCellular') + ' Illustration'"
                    :src="require('@/assets/images/G3 device.svg')"
                  />
                </b-col>
                <b-col data-test-hook="G3Btn">
                  <strong> {{ $t('xConnectivityAccessoryCellular') }}</strong>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-card
            class="mb-3 hoverPointer"
            :class="selectedDeviceType('eth')"
            @click="setDeviceType('eth')"
            data-test-hook="eth-clickable-card-step6-enrollment"
          >
            <b-card-body>
              <b-row align-v="center">
                <b-col cols="2" class="text-center">
                  <b-img
                    class="device-type-img"
                    :alt="$t('xWifiEth') + ' Illustration'"
                    :src="require('@/assets/images/tether-wifi.svg')"
                  />
                </b-col>
                <b-col data-test-hook="ETHBtn">
                  <strong> {{ $t('xWifiEth') }}</strong>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row v-if="canHavePaidSubscription(accountData)">
        <b-col>
          <b-card
            class="mb-0 hoverPointer"
            :class="selectedDeviceType('lte')"
            @click="setDeviceType('lte')"
            data-test-hook="lte-clickable-card-step6-enrollment"
          >
            <b-card-body>
              <b-row align-v="center">
                <b-col cols="2" class="text-center">
                  <b-img
                    class="device-type-img"
                    :alt="$t('xLteCellular') + ' Illustration'"
                    :src="require('@/assets/images/lte-cellular.svg')"
                  />
                </b-col>
                <b-col data-test-hook="LTEBtn">
                  <strong> {{ $t('xLteCellular') }}</strong>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="mt-5" align-v="center">
        <b-col class="mr-0 pr-0">
          <b-progress :value="66.66" variant="primary"></b-progress>
        </b-col>
        <b-col cols="auto" class="ml-0 pl-0">
          <i class="fas fa-shield-check fa-lg inProgress ml-1 pl-0" />
        </b-col>
      </b-row>
    </b-modal>

    <b-modal id="no-wifi-module-modal" size="lg" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pr-0">
            <b-button
              variant="icon-button"
              v-b-modal.select-device-type-step-6
              data-test-hook="back-button-noWifiModule-enrollment"
            >
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2 class="modal-title"><i class="fas fa-plus colorOrange mr-1" /> {{ $t('xAddCommunicationDevice') }}</h2>
          </b-col>
        </b-row>
      </template>
      <template #modal-footer>
        <b-button
          variant="outline-warning"
          @click="logComeBackLater(), closeEnrollmentModals()"
          data-test-hook="come-back-later-noWifiModule-enrollment"
        >
          {{ $t('xComeBackLater') }}
        </b-button>
        <b-button
          variant="primary"
          @click="logContinueWithSetup(), $bvModal.show('enter-device-info-step-7')"
          data-test-hook="continue-button-noWifiModule-enrollment"
        >
          {{ $t('xContinueWithSetup') }}
        </b-button>
      </template>
      <b-row>
        <b-col>
          {{ $t('xYourGeneratorDoesNotHaveWiFi') }}
          <b-link
            href="https://www.generac.com/wifikit"
            target="_blank"
            data-test-hook="wifikit-link-noWifiModule-enrollment"
          >
            {{ $t('xHere') }}
          </b-link>
          {{ $t('xAndWeWillShipOneToYouWhen') }}
        </b-col>
      </b-row>
    </b-modal>

    <b-modal id="enter-device-info-step-7" size="lg" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pr-0">
            <b-button
              variant="icon-button"
              v-b-modal.select-device-type-step-6
              data-test-hook="back-button-step7-enrollment"
            >
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2 class="modal-title"><i class="fas fa-plus colorOrange mr-1" /> {{ $t('xAddCommunicationDevice') }}</h2>
          </b-col>
        </b-row>
      </template>
      <template #modal-footer>
        <b-button
          variant="outline-warning"
          @click="closeEnrollmentModals('enter-apparatus-device-info')"
          data-test-hook="cancel-button-step7-enrollment"
        >
          {{ $t('xCancel') }}
        </b-button>
        <b-button
          variant="primary"
          @click="validateDevice()"
          :disabled="deviceInfoEntered()"
          data-test-hook="ConfirmBtn-step7-enrollment"
        >
          {{ $t('xConfirm') }} <i class="fas fa-arrow-right" />
          <b-spinner class="ml-2" v-if="validatingDevice"></b-spinner>
        </b-button>
      </template>
      <div v-if="deviceType == 'wifi'">
        <b-row>
          <b-col>
            <h5>
              <strong>{{ $t('xEnterYourMacAddress') }}</strong>
            </h5>
          </b-col>
          <b-col cols="auto" class="float-right text-right">
            <i class="fas fa-info-circle fa-lg colorOrange hoverPointer" v-b-modal.mac-address-help />
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-input-group>
              <b-form-input
                maxlength="17"
                v-model="macAddress"
                v-mask="'NN:NN:NN:NN:NN:NN'"
                autocomplete="new-password"
                data-test-hook="MACField"
              />
            </b-input-group>
          </b-col>
        </b-row>
      </div>
      <div v-else>
        <b-row>
          <b-col>
            <h5>
              <strong>{{ $t('xEnterYourDeviceSN') }}</strong>
            </h5>
          </b-col>
          <b-col cols="auto" class="float-right text-right">
            <i class="fas fa-info-circle fa-lg colorOrange hoverPointer" v-b-modal.tether-serial-number-help />
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-input-group>
              <b-form-input
                v-if="deviceType == hsbG3DeviceType"
                maxlength="9"
                v-model="shortDeviceId"
                autocomplete="new-password"
                data-test-hook="SerialNumberField"
              />
              <b-form-input
                v-else
                maxlength="10"
                v-model="searchLabel"
                autocomplete="new-password"
                data-test-hook="SerialNumberField"
              />
            </b-input-group>
          </b-col>
        </b-row>
      </div>
      <div v-if="showValidateDeviceError" :show="showValidateDeviceError" class="alert alert--validation alert-danger">
        {{ validateDeviceErrorMessage }}
      </div>
      <b-row class="mt-5" align-v="center">
        <b-col class="mr-0 pr-0">
          <b-progress :value="77.77" variant="primary"></b-progress>
        </b-col>
        <b-col cols="auto" class="ml-0 pl-0">
          <i class="fas fa-shield-check fa-lg inProgress ml-1 pl-0" />
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="mac-address-help"
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      hide-footer
      centered
    >
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pr-0">
            <b-button
              variant="icon-button"
              v-b-modal.enter-device-info-step-7
              data-test-hook="back-button-macAddressHelp-enrollment"
            >
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2 class="modal-title">
              {{ $t('xMacAddressHelp') }}
            </h2>
          </b-col>
        </b-row>
      </template>
      <b-row>
        <b-col>
          <b-carousel class="py-3 px-3" background="lightgray" :controls="true" :indicators="true" :interval="0">
            <b-carousel-slide :img-src="require('@/assets/images/gen-onboard-wifi-activate.svg')" />
            <b-carousel-slide :img-src="require('@/assets/images/wifi-label.svg')" />
          </b-carousel>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col>
          <strong>{{ $t('xLocateTheMACAddress') }}</strong>
        </b-col>
      </b-row>
      <b-row>
        <b-col>{{ $t('xOpenTheCoverOfYourGenerator') }}</b-col>
      </b-row>
      <b-row>
        <b-col>{{ $t('xRemoveTheFrontPanel') }}</b-col>
      </b-row>
      <b-row>
        <b-col>{{ $t('xFindTheLabelsLocated') }}</b-col>
      </b-row>
      <b-row>
        <b-col>{{ $t('xTheMACAddressAppearsOnTheWIFILabel') }}</b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="tether-serial-number-help"
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      hide-footer
      centered
    >
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pr-0">
            <b-button
              variant="icon-button"
              v-b-modal.enter-device-info-step-7
              data-test-hook="back-button-tetherSNHelp-enrollment"
            >
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2 class="modal-title">
              {{ $t('xSNHelp') }}
            </h2>
          </b-col>
        </b-row>
      </template>
      <b-row class="py-5 mt-0 px-3" style="background-color: #EEEEEE;">
        <b-col v-if="deviceType == 'lte'">
          <b-img class="sn-tag-img" fluid :src="require('@/assets/images/lte-label.svg')" />
        </b-col>
        <b-col v-else-if="deviceType == hsbG3DeviceType">
          <b-img class="sn-tag-img" fluid :src="require('@/assets/images/g3-label.svg')" />
        </b-col>
        <b-col v-else>
          <b-img class="sn-tag-img" fluid :src="require('@/assets/images/wifi-eth-label.svg')" />
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col>
          <strong>{{ $t('xLocateTheDeviceSerialNumber') }}</strong>
        </b-col>
      </b-row>
      <b-row v-if="deviceType == 'lte'">
        <b-col>
          {{ $t('x1LocateTheLabelOnTheBottomOfYourLTECellularDevice') }}
        </b-col>
      </b-row>
      <b-row v-else-if="deviceType == hsbG3DeviceType">
        <b-col>
          {{ $t('x1TheDeviceIDIsFoundOnTheBackOfTheCellularDevice') }}
        </b-col>
      </b-row>
      <b-row v-else>
        <b-col>
          {{ $t('x1LocateTheLabelOnTheBottomOfYourWiFiEthernetDevice') }}
        </b-col>
      </b-row>
      <b-row v-if="deviceType == 'lte'">
        <b-col>{{ $t('x2EnterYourDeviceSerialNumber') }}</b-col>
      </b-row>
      <b-row v-else-if="deviceType == hsbG3DeviceType">
        <b-col>{{ $t('x2TheSerialNumberIsLocatedDirectlyBelowTheQRCode') }}</b-col>
      </b-row>
      <b-row v-else>
        <b-col>{{ $t('x2EnterYourDeviceS/N') }}</b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="enroll-device-step-8"
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      hide-header
      centered
    >
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pl-0">
            <b-button
              variant="icon-button"
              v-b-modal.enter-device-info-step-7
              data-test-hook="back-button-step8-enrollment"
            >
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2>{{ $t('xConfirmDeviceEnrollment') }}</h2>
          </b-col>
        </b-row>
      </template>
      <template #modal-footer>
        <b-button
          variant="outline-warning"
          @click="closeEnrollmentModals('confirm-apparatus-device')"
          data-test-hook="cancel-button-step8-enrollment"
        >
          {{ $t('xCancel') }}
        </b-button>
        <b-button
          variant="primary"
          @click="enrollApparatusDevice()"
          :disabled="enrollingDevice"
          data-test-hook="EnrollBtn-step8-enrollment"
        >
          {{ $t('xEnrollThisDevice') }} <i class="fas fa-arrow-right" />
          <b-spinner class="ml-2" v-if="enrollingDevice"></b-spinner>
        </b-button>
      </template>
      <b-row class="text-center" v-if="validatedDeviceInfo">
        <b-col v-if="validatedDeviceInfo.deviceType == 'wifi'">
          <b-img height="200px" :src="require('@/assets/images/onboard-wifi.svg')" />
        </b-col>
        <b-col v-if="validatedDeviceInfo.deviceType == hsbG3DeviceType">
          <b-img height="200px" :src="require('@/assets/images/onboard-wifi.svg')" />
        </b-col>
        <b-col v-if="validatedDeviceInfo.deviceType == 'lte'">
          <b-img height="200px" :src="require('@/assets/images/lte-cellular.svg')" />
        </b-col>
        <b-col v-if="validatedDeviceInfo.deviceType == 'eth'">
          <b-img height="200px" :src="require('@/assets/images/tether-wifi.svg')" />
        </b-col>
      </b-row>
      <b-row class="text-center">
        <b-col class="mt-3">
          <h2>{{ $t('xYourCommunicationDeviceInfo') }}</h2>
        </b-col>
      </b-row>
      <b-row class="text-center">
        <b-col v-if="deviceType == 'wifi'">
          <strong>
            {{ 'xMacAddress' | formatLabel }}
          </strong>
          {{ macAddress }}
        </b-col>
        <b-col v-else-if="deviceType == hsbG3DeviceType">
          <strong>
            {{ 'xDeviceSerialNumber' | formatLabel }}
          </strong>
          {{ shortDeviceId }}
        </b-col>
        <b-col v-else>
          <strong>
            {{ 'xDeviceSerialNumber' | formatLabel }}
          </strong>
          {{ searchLabel }}
        </b-col>
      </b-row>
      <div
        v-if="showEnrollDeviceError && enrollDeviceErrorMessage"
        :show="showEnrollDeviceError"
        class="alert alert-danger alert--validation"
      >
        {{ enrollDeviceErrorMessage }}
      </div>
      <b-row class="mt-5" align-v="center">
        <b-col class="mr-0 pr-0">
          <b-progress :value="88.88" variant="primary"></b-progress>
        </b-col>
        <b-col cols="auto" class="ml-0 pl-0">
          <i class="fas fa-shield-check fa-lg inProgress ml-1 pl-0" />
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="device-enrollment-success-step-9"
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      hide-header
      centered
    >
      <template #modal-footer>
        <b-button
          variant="outline-warning"
          v-b-modal.add-transfer-switch
          @click="
            trackAppInsightsEvent('open-register-transfer-switch-modal', {
              page: 'enrollment',
              organizationId: accountData.organizationId,
              userId: accountData.userId
            })
          "
          data-test-hook="add-ts-button-step9-enrollment"
        >
          <i class="fas fa-plus" />
          {{ $t('xTransferSwitch') }}
        </b-button>
        <b-button
          v-if="canHavePaidSubscription(accountData)"
          variant="primary"
          @click="openSubscriptionModal()"
          data-test-hook="SubscriptionBtn"
        >
          <i class="far fa-badge-dollar" />
          {{ $t('xSubscribe') }}
          <b-spinner class="ml-2" v-if="gettingSubscriptions"></b-spinner>
        </b-button>
        <b-button v-else variant="primary" @click="closeEnrollmentModals()" data-test-hook="DoneBtn">
          {{ $t('xDone') }}
        </b-button>
      </template>
      <b-row class="text-center mb-3">
        <b-col>
          <h1>{{ $t('xSuccess') }}!</h1>
          <h5>{{ $t('xYourCommunicationDeviceHasSuccessfullyBeenEnrolledOntoYourGenerator') }}</h5>
        </b-col>
      </b-row>
      <b-row class="text-center">
        <b-col>
          <b-img
            class="generator-success-img"
            alt="Generator Illustration"
            fluid
            :src="require('@/assets/images/gen-enrollment-success.svg')"
          />
        </b-col>
      </b-row>

      <b-row class="mt-5" align-v="center">
        <b-col class="mr-0 pr-0">
          <b-progress :value="100" variant="primary"></b-progress>
        </b-col>
        <b-col cols="auto" class="ml-0 pl-0">
          <i class="fas fa-shield-check fa-lg colorOrange ml-2 pl-0" />
        </b-col>
      </b-row>
      <div
        v-if="showGetSubscriptionInfoError && getSubscriptionInfoErrorMessage"
        :show="showGetSubscriptionInfoError"
        class="alert alert--validation alert-danger"
      >
        {{ getSubscriptionInfoErrorMessage }}
      </div>
    </b-modal>

    <b-modal id="add-transfer-switch" size="lg" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <b-row align-v="center">
          <b-col cols="auto" class="pr-0">
            <b-button
              variant="icon-button"
              v-b-modal.device-enrollment-success-step-9
              @click=";(sns = []), (snToAdd = null)"
              data-test-hook="back-button-addTS-enrollment"
            >
              <i class="fas fa-chevron-left back-chevron-button" />
            </b-button>
          </b-col>
          <b-col>
            <h2 class="modal-title"><i class="far fa-plus colorOrange mr-2"></i>{{ $t('xAddTransferSwitch') }}</h2>
          </b-col>
        </b-row>
      </template>
      <template #modal-footer>
        <b-button
          block
          variant="primary"
          :disabled="!canRegisterSns()"
          @click="
            registerTransferSwitches(),
              trackAppInsightsEvent('register-transfer-switch-serial-numbers', {
                page: 'enrollment',
                organizationId: accountData.organizationId,
                userId: accountData.userId,
                serialNumbers: sns
              })
          "
          data-test-hook="confirm-button-addTS-enrollment"
        >
          {{ $t('xConfirm') }}
          <b-spinner class="mx-1" v-if="registeringSerialNumbers" />
          <i class="far fa-arrow-right" />
        </b-button>
      </template>
      <b-row v-for="(sn, index) in sns" :key="index">
        <b-col>
          <b-row>
            <b-col>
              <h4>{{ ($t('xTransferSwitch') + ' ' + (index + 1)) | formatLabel }}</h4>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="asterisk-text">{{ $t('xSerialNumber') }}</b-col>
            <b-col cols="12">
              <h4 style="word-wrap: break-word;">{{ sn }}</h4>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <h4>
            {{ $t('xEnterTransferSwitch') }}
            <span v-if="sns.length > 0">{{ sns.length + 1 }}</span>
          </h4>
        </b-col>
        <b-col>
          <b-form-group>
            <b-form-input v-model="snToAdd" maxlength="20" :placeholder="$t('xSerialNumber')" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button
            variant="outline-warning"
            :disabled="!snToAdd || snToAdd.length < 7"
            block
            @click="addSnToRegistrationList()"
            data-test-hook="add-TS-button-addTS-enrollment"
          >
            <i class="fas fa-plus" />
            {{ $t('xTransferSwitch') }}
          </b-button>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="transfer-serial-number-registration-success"
      size="lg"
      hide-header
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      centered
    >
      <template #modal-footer>
        <b-button
          v-if="canHavePaidSubscription(accountData)"
          block
          variant="primary"
          @click="openSubscriptionModal()"
          data-test-hook="SubscriptionBtn"
        >
          <i class="far fa-badge-dollar" />
          {{ $t('xUpgradePremiumMonitoring') }}
          <b-spinner class="ml-2" v-if="gettingSubscriptions"></b-spinner>
        </b-button>
        <b-button v-else variant="primary" @click="closeEnrollmentModals()" data-test-hook="DoneBtn">
          {{ $t('xDone') }}
        </b-button>
      </template>
      <b-row class="text-center">
        <b-col>
          <span style="font-size: 15rem;"><i class="far fa-thumbs-up colorOrange"/></span>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col>
          <h3 class="colorOrange">{{ $t('xAwesome') }}</h3>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          {{ $t('xTransferSwitchSuccess') }}
        </b-col>
      </b-row>
      <div
        v-if="showGetSubscriptionInfoError && getSubscriptionInfoErrorMessage"
        :show="showGetSubscriptionInfoError"
        class="alert alert--validation alert-danger"
      >
        {{ getSubscriptionInfoErrorMessage }}
      </div>
    </b-modal>

    <b-modal
      id="transfer-serial-number-registration-failure"
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      centered
      hide-header
    >
      <template #modal-footer>
        <b-button
          v-if="canHavePaidSubscription(accountData)"
          block
          variant="primary"
          @click="openSubscriptionModal()"
          data-test-hook="SubscriptionBtn"
        >
          <i class="far fa-badge-dollar" />
          {{ $t('xUpgradePremiumMonitoring') }}
          <b-spinner class="ml-2" v-if="gettingSubscriptions"></b-spinner>
        </b-button>
        <b-button v-else variant="primary" @click="closeEnrollmentModals()" data-test-hook="DoneBtn">
          {{ $t('xDone') }}
        </b-button>
      </template>
      <b-row class="text-center">
        <b-col class="my-3">
          <span style="font-size: 15rem;"><i class="far fa-sad-tear colorOrange"/></span>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <h3 class="colorOrange">{{ $t('xWhoops') }}</h3>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          {{ transferSwitchFailureMessage }}
        </b-col>
      </b-row>
      <div
        v-if="showGetSubscriptionInfoError && getSubscriptionInfoErrorMessage"
        :show="showGetSubscriptionInfoError"
        class="alert alert--validation alert-danger"
      >
        {{ getSubscriptionInfoErrorMessage }}
      </div>
    </b-modal>

    <SubscriptionsModal
      modal-id="subscription-modal-hsb"
      :apparatus-id="generatorId"
      :apparatus-list="apparatuses"
      :account-profile="accountProfile"
      @setSelectedProduct="setSelectedProduct"
      @setSubscriptionPollingInfo="setSubscriptionPollingInfo"
    />

    <Modal modalId="subscription-success">
      <ModalHeader :title="$t('xCongratulations') + '!'" icon="colorOrange fal fa-badge-dollar">
        <button
          class="btn close"
          @click="closeEnrollmentModals(), trackAnalytics('subscription-success-modal-cancel-button-clicked')"
          data-test-hook="modalXCloseBtn"
        >
          <i class="fas fa-times" />
        </button>
      </ModalHeader>
      <ModalBody>
        <div data-test-hook="modalBodyText1">
          {{ $t('xYourGeneratorIsSubscribedForRemoteMonitoring') }}
        </div>
        <div v-if="selectedFuelType == 'LP' && !doesAccountHaveTank">
          <div style="font-size:1rem;font-weight:600" class="mt-3" data-test-hook="modalBodyText2">
            {{ $t('xYourGeneratorIsRunningOnPropane') }}
          </div>
          <div data-test-hook="modalBodyText3">
            {{ $t('xFeelEvenMoreSecureKnowing') }}
          </div>
          <div class="row ml-1">
            <div
              class="mt-3 btn"
              @click="beginTankEnrollmentFlow()"
              style="font-size:1rem;font-weight:600; padding-top:0px; border-top-width:0px; padding-left:0px; padding-right:0px; margin-top:10px;"
            >
              <span class="colorRed fas fa-plus-circle mr-1" style="font-size: 1rem;"></span>
              <span style="text-transform:none !important" data-test-hook="modalAddATankBtn">
                {{ $t('xAddATankMonitor') }}
              </span>
            </div>
            <div class="ml-2" style="padding-top:17px">
              {{ $t('xOR') | lowercase }}
            </div>
            <div class="ml-2" style="padding-top:16px" data-test-hook="modalLearnAboutTanksBtn">
              <a
                href="https://www.generac.com/for-homeowners/mobile-link/propane-monitoring"
                target="_blank"
                style="text-decoration: underline"
                @click="trackAnalytics('subscription-success-modal-tank-monitor-info-clicked')"
              >
                {{ $t('xLearnMoreAboutTankMonitors') }}<i class="ml-1 fas fa-external-link"></i
              ></a>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div
          class="btn btn-primary"
          @click="trackAnalytics('subscription-success-modal-view-gen-details-clicked'), closeEnrollmentModals()"
          data-test-hook="ReturnDashButton"
        >
          {{ $t('xReturnToDashboard') }}
        </div>
      </ModalFooter>
    </Modal>

    <b-modal id="fuel-monitor-limit-reached" size="m" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <b-row class="w-100" align-v="center" no-gutters>
          <b-col>
            <h2 class="modal-title">
              <i class="fas fa-exclamation-triangle colorOrange mr-1" />
              {{ $t('xLimitReached') }}
            </h2>
          </b-col>
          <b-col cols="auto" class="float-right">
            <b-button
              class="btn-icon-button--gray"
              variant="icon-button"
              @click="closeEnrollmentModals()"
              data-test-hook="x-button-fuel-monitor-reached-modal"
            >
              <i class="fas fa-times" />
            </b-button>
          </b-col>
        </b-row>
      </template>
      <b-row class="modal-body">
        {{ $t('xFuelMonitorLimitText') }}
      </b-row>
      <template #modal-footer class="w-50">
        <b-button
          variant="primary"
          data-test-hook="learn-more-fuel-tank-limit-reached-enrollment"
          @click="closeEnrollmentModals(), learnMoreClicked()"
        >
          {{ $t('xLearnMoreLowercase') }}
        </b-button>
      </template>
    </b-modal>

    <FuelMonitorInvalidRegionModal
      modalId="fuel-monitors-not-allowed-for-your-region"
      @onClose="closeEnrollmentModals()"
    >
      <b-button
        variant="primary"
        data-test-hook="fuel-monitors-not-allowed-for-your-region-done-button"
        @click="closeEnrollmentModals()"
      >
        {{ $t('xDone') }}
      </b-button>
    </FuelMonitorInvalidRegionModal>

    <b-modal id="hsb-limit-reached" size="s" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <b-row align-v="center" class="w-100" no-gutters>
          <b-col>
            <h2 class="modal-title">
              <i class="fas fa-exclamation-triangle colorOrange mr-1" />
              {{ $t('xLimitReached') }}
            </h2>
          </b-col>
          <b-col cols="auto" class="float-right">
            <b-button
              class="btn-icon-button--gray"
              variant="icon-button"
              @click="closeEnrollmentModals()"
              data-test-hook="x-button-hsb-limit-modal"
            >
              <i class="fas fa-times" />
            </b-button>
          </b-col>
        </b-row>
      </template>

      <b-row class="modal-body">
        {{ $t('xHSBLimitText') }}
      </b-row>
      <template #modal-footer>
        <b-button
          variant="primary"
          data-test-hook="learn-more-fuel-tank-limit-reached-enrollment"
          @click="closeEnrollmentModals(), learnMoreClicked()"
        >
          {{ $t('xLearnMoreLowercase') }}
        </b-button>
      </template>
    </b-modal>

    <b-modal id="finish-setup-error-modal" size="s" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <b-row align-v="center" class="w-100 pb-0" no-gutters>
          <b-col>
            <h2 class="modal-title">
              <i class="far fa-exclamation-circle colorOrange mr-1" />
              {{ $t('xYourAccountNeedsAttention') }}
            </h2>
          </b-col>
          <b-col cols="auto" class="float-right">
            <b-button
              class="btn-icon-button--gray"
              variant="icon-button"
              @click="closeEnrollmentModals()"
              data-test-hook="x-button-finish-setup-error-modal"
            >
              <i class="fas fa-times" />
            </b-button>
          </b-col>
        </b-row>
      </template>

      <b-row class="modal-body pb-0 pt-0">
        {{ $t('xFinishProductSetup') }}
      </b-row>
      <template #modal-footer>
        <b-col class="justify-content-center">
          <b-button
            variant="primary"
            data-test-hook="contact=support-setup-error-modal"
            @click="closeEnrollmentModals(), navToSupportPage()"
            class="w-100"
          >
            {{ $t('xContactSupportUpgrade') }}
          </b-button>
        </b-col>
      </template>
    </b-modal>

    <b-modal id="fix-plan-error-modal" size="s" no-close-on-backdrop no-close-on-esc no-fade no-stacking centered>
      <template #modal-header>
        <b-row align-v="center" class="w-100" no-gutters>
          <b-col>
            <h2 class="modal-title">
              <i class="far fa-exclamation-circle colorOrange mr-1" />
              {{ $t('xYourAccountNeedsAttention') }}
            </h2>
          </b-col>
          <b-col cols="auto" class="float-right">
            <b-button
              class="btn-icon-button--gray"
              variant="icon-button"
              @click="closeEnrollmentModals()"
              data-test-hook="x-button-fix-plan-error-modal"
            >
              <i class="fas fa-times" />
            </b-button>
          </b-col>
        </b-row>
      </template>

      <b-row class="modal-body pt-0 pb-0">
        {{ $t('xFixPlanIssues') }}
      </b-row>
      <template #modal-footer>
        <b-col class="d-flex justify-content-center">
          <b-button
            variant="primary"
            data-test-hook="upgrade-to-premium-fix-plan-error-modal"
            @click="closeEnrollmentModals(), navToPlanDetailsPage()"
            class="w-100"
          >
            {{ $t('xUpgradePremium') }}
          </b-button>
        </b-col>
      </template>
    </b-modal>

    <b-modal
      id="not-allowed-to-enroll-legacy-sub"
      size="s"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      centered
    >
      <template #modal-header>
        <b-row align-v="center" class="w-100" no-gutters>
          <b-col>
            <h2 class="modal-title">
              <i class="fas fa-exclamation-triangle colorOrange mr-1" />
              {{ $t('xLimitReached') }}
            </h2>
          </b-col>
          <b-col cols="auto" class="float-right">
            <b-button
              class="btn-icon-button--gray"
              variant="icon-button"
              @click="closeEnrollmentModals()"
              data-test-hook="x-button-legacy-sub-modal"
            >
              <i class="fas fa-times" />
            </b-button>
          </b-col>
        </b-row>
      </template>

      <b-row class="modal-body">
        {{ $t('xUnableToAddDueToLegacySub') }}
      </b-row>
      <template #modal-footer>
        <b-button
          variant="primary"
          data-test-hook="support-can-not-enroll-with-legacy-sub"
          @click="closeEnrollmentModals(), learnMoreClicked()"
        >
          {{ $t('xLearnMoreLowercase') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import * as Constants from '@/constants'
import SubscriptionsModal from './Subscription/SubscriptionsModal.vue'
import { pollForSubmissionUpdate, subscriptionActive } from '../../utils/mixins'
import AddressSearchModals from '../Common/Address/AddressSearchModals.vue'
import FuelMonitorInvalidRegionModal from './FuelMonitorInvalidRegionModal.vue'

export default {
  props: ['basicGenWithExistingSub', 'apparatusId', 'modelNumber'],
  components: { AddressSearchModals, SubscriptionsModal, FuelMonitorInvalidRegionModal },
  data() {
    return {
      userEnteredSerialNumber: null,
      generatorName: null,
      useAccountAddress: true,
      consent: null,
      generatorAddress: null,
      deviceType: null,
      macAddress: null,
      searchLabel: null,
      dualGenSerialNumber: null,
      validatingDevice: false,
      validatedDeviceInfo: null,
      newApparatusInfo: null,
      enrollingDevice: false,
      validatingSN: false,
      enrollingApparatus: false,
      validateSNError: null,
      enrollDeviceError: null,
      gettingSubscriptions: false,
      existingSubscriptions: [],
      previousSubscriptionModal: null,
      annualPaidProduct: null,
      monthlyPaidProduct: null,
      freeProduct: null,
      selectedProduct: null,
      planIsSelected: false,
      selectedProductCostPreview: null,
      gettingPaymentInfo: false,
      paying: false,
      existingPaymentInfo: null,
      useExistingPaymentInfo: true,
      creditCardNumber: null,
      expirationMonth: null,
      expirationYear: null,
      cvv: null,
      showCreditCardValidationBanner: false,
      accountData: null,
      useExistingBillingInformation: true,
      editingAddress: false,
      savingAccountHolder: false,
      apparatusModelNumber: null,
      apparatusDescription: null,
      addressSaveDisabled: false,
      errorMessages: [],
      registeringSerialNumbers: false,
      sns: [],
      snToAdd: null,
      transferSwitchFailureMessage: null,
      activeSubscriptionModal: null,
      yearlyTabActive: true,
      monthlyTabActive: false,
      showSavingAccountHolderError: false,
      showPayForProductError: false,
      payForProductErrorMessage: null,
      showSelectSubError: false,
      showGetSubscriptionInfoError: false,
      getSubscriptionInfoErrorMessage: null,
      showSerialNumberValidationError: false,
      serialNumberValidationMessage: null,
      showEnrollApparatusError: false,
      showRegistrationFailedError: false,
      showValidateDeviceError: false,
      validateDeviceErrorMessage: null,
      showEnrollDeviceError: false,
      enrollDeviceErrorMessage: null,
      shortDeviceId: null,
      selectedFuelType: null,
      selectedFuelTypeName: null,
      selectedFuelTypeNumber: null,
      fuelOptions: [
        { value: 'LP', text: 'Liquid Propane (LP)' },
        { value: 'NaturalGas', text: 'Natural Gas' },
        { value: 'Diesel', text: 'Diesel' }
      ],
      doesAccountHaveTank: false,
      subscriptionPollingInfo: null,
      isShowingValidationView: false,
      saving: false,
      addressFormDisabled: false
    }
  },
  computed: {
    ...mapState({
      ...mapState({
        currentApparatusDetails: state => state.apparatus.currentApparatusDetails
      }),
      apparatuses: state => state.apparatus.apparatuses,
      accountProfile: state => state.account.accountProfile,
      freeFeatures: state => state.application.freeFeatures,
      paidFeatures: state => state.application.paidFeatures,
      isImpersonating: state => state.auth.isImpersonating,
      currentAppConfig: state => state.application.currentAppConfig
    }),
    freeWifi() {
      return Constants.FreeWifiEthProductId
    },
    g3FeatureFlag() {
      return Constants.G3DeviceFeatureFlag
    },
    hsbG3DeviceType() {
      return Constants.HsbG3DeviceType
    },
    generatorId() {
      if (this.apparatusId) {
        return this.apparatusId
      }
      if (this.newApparatusInfo) {
        return this.newApparatusInfo.apparatusId
      }
      return this.currentApparatusDetails?.apparatusId
    },
    showValidationView() {
      return this.isShowingValidationView
    },
    confirmAddressDisabled() {
      return !this.$refs.genEnrollmentAddressSearchModalsRef?.selectedAddress
    },
    showContactSupportButton() {
      return this.$refs.genEnrollmentAddressSearchModalsRef?.showContactSupportButton
    },
    continueButtonDisabled() {
      if (!this.useAccountAddress) {
        return this.addressFormDisabled
      }
      return false
    }
  },
  methods: {
    handleSetIsNextDisabled(isDisabled) {
      this.addressFormDisabled = isDisabled
    },
    updateGeneratorAddress(newAddress, responseOptions) {
      this.saving = true
      if (newAddress) {
        // We need to cast the new validated address to work with enrollment
        this.generatorAddress = {
          city: newAddress.city,
          contacts: this.accountProfile.address.contacts, // Carry account contacts from profile
          country: newAddress.countryCode,
          line1: newAddress.address1,
          line2: newAddress.address2 ? newAddress.address2 : null,
          postalCode: newAddress.postalCode,
          region: newAddress.state,
          type: this.accountProfile.address.type // Carry address type from profile
        }
        // saved the address, now we can navigate to the next enrollment step
        this.$bvModal.show('confirm-gen-enrollment-step-5')
      } else {
        responseOptions.openAddressFormWithError()
      }
      this.saving = false
    },
    addGeneratorAddress() {
      if (this.useAccountAddress) {
        this.$bvModal.show('confirm-gen-enrollment-step-5')
      } else {
        if (!this.continueButtonDisabled) {
          // If the continue button should be disabled, we don't want to navigate to validation form
          this.$refs.genEnrollmentAddressSearchModalsRef.openAddressFormModal()
          this.$refs.genEnrollmentAddressSearchModalsRef.verifyAddress()
          this.handleValidationStateChange(true)
        }
      }
    },
    handleValidationStateChange(isShowing) {
      this.isShowingValidationView = isShowing
    },
    editAddress() {
      this.handleValidationStateChange(false)
      this.$refs.genEnrollmentAddressSearchModalsRef.openAddressFormModal()
      this.$refs.genEnrollmentAddressSearchModalsRef.goBackToAddressForm()
    },
    confirmAddress() {
      this.$refs.genEnrollmentAddressSearchModalsRef.saveAndSubmit()
    },
    setFuelType() {
      if (this.selectedFuelType == 'LP') {
        this.selectedFuelTypeName = 'Liquid Propane (LP)'
        this.selectedFuelTypeNumber = 0
      } else if (this.selectedFuelType == 'NaturalGas') {
        this.selectedFuelTypeName = 'Natural Gas'
        this.selectedFuelTypeNumber = 1
      } else {
        this.selectedFuelTypeName = 'Diesel'
        this.selectedFuelTypeNumber = 2
      }
    },
    logContinueWithSetup() {
      let appId = null
      if (this.apparatusId) {
        appId = this.apparatusId
      } else {
        appId = this.newApparatusInfo.apparatusId
      }
      this.$appInsights.trackEvent({
        name: 'user-continuing-with-enrollment',
        properties: {
          page: 'dashboard',
          modal: 'no-wifi-module-modal',
          apparatusId: appId,
          organizationId: this.accountProfile.organizationId,
          userId: this.accountProfile.userId,
          userId: this.accountProfile.userId,
          modelNumber: this.apparatusModelNumber
        }
      })
    },
    logComeBackLater() {
      let appId = null
      if (this.apparatusId) {
        appId = this.apparatusId
      } else {
        appId = this.newApparatusInfo.apparatusId
      }
      this.$appInsights.trackEvent({
        name: 'user-coming-back-later',
        properties: {
          page: 'dashboard',
          modal: 'no-wifi-module-modal',
          apparatusId: appId,
          organizationId: this.accountProfile.organizationId,
          userId: this.accountProfile.userId,
          modelNumber: this.apparatusModelNumber
        }
      })
    },
    enterDeviceInformation() {
      if (
        this.deviceType == 'wifi' &&
        this.apparatusModelNumber &&
        this.genWithoutWifiModule(this.apparatusModelNumber)
      ) {
        let appId = null
        if (this.apparatusId) {
          appId = this.apparatusId
        } else {
          appId = this.newApparatusInfo.apparatusId
        }
        this.$appInsights.trackEvent({
          name: 'modal-for-gen-with-no-wifi-module',
          properties: {
            page: 'dashboard',
            modal: 'no-wifi-module-modal',
            apparatusId: appId,
            organizationId: this.accountProfile.organizationId,
            userId: this.accountProfile.userId,
            modelNumber: this.apparatusModelNumber
          }
        })
        this.$bvModal.show('no-wifi-module-modal')
      } else {
        this.$bvModal.show('enter-device-info-step-7')
      }
    },
    getSubscriptionSavings(annual, monthly) {
      if (annual && monthly) return Math.round(((monthly.cost * 12 - annual.cost) / (monthly.cost * 12)) * 100)
      return 0
    },
    setSelectedProduct(product) {
      this.selectedProduct = product
    },
    openSubscriptionModal() {
      this.$bvModal.show('subscription-modal-hsb')
    },
    validateSerialNumber() {
      if (!this.userEnteredSerialNumber) {
        this.serialNumberValidationMessage = this.$t('xYouMustEnterAValidSerialNumberToContinue')
        this.showSerialNumberValidationError = true
        return false
      }
      this.validatingSN = true
      this.$store
        .dispatch('ValidateSerialNumber', this.userEnteredSerialNumber)
        .then(response => {
          this.validatingSN = false
          this.showSerialNumberValidationError = false
          this.serialNumberValidationMessage = null
          if (response.data.success) {
            this.apparatusModelNumber = response.data.modelNumber
            this.apparatusDescription = response.data.description
            this.$bvModal.show('gen-name-step-2')
          } else {
            this.serialNumberValidationMessage = response.data.message
            this.showSerialNumberValidationError = true
          }
        })
        .catch(error => {
          this.validatingSN = false
          this.validateSNError = error.response.data
          this.serialNumberValidationMessage = this.$t('xDisabledOwnerOrgNotDealerManagedError')
          this.showSerialNumberValidationError = true
        })
    },
    enrollApparatus() {
      this.enrollingApparatus = true
      let address = this.useAccountAddress ? this.accountProfile.address : this.generatorAddress
      this.$store
        .dispatch('EnrollApparatus', {
          organizationId: this.accountProfile.organizationId,
          serialNumber: this.userEnteredSerialNumber,
          apparatusName: this.generatorName,
          apparatusAddress: address,
          apparatusType: 0,
          apparatusModelNumber: this.apparatusModelNumber,
          apparatusDescription: this.apparatusDescription,
          fuelType: this.selectedFuelTypeNumber
        })
        .then(response => {
          this.enrollingApparatus = false
          this.newApparatusInfo = response.data
          if (!response.data.registrationSuccess) {
            this.showRegistrationFailedError = true
            this.$appInsights.trackEvent({
              name: 'enrollment-registration-failure',
              properties: {
                orgId:
                  this.accountProfile && this.accountProfile.organizationId ? this.accountProfile.organizationId : null,
                userId: this.accountProfile.userId,
                apparatusId: response.data.apparatusId,
                serialNumber: this.userEnteredSerialNumber
              }
            })
          }
          if (response.data.hasValidDevice) {
            this.deviceType = response.data.device.deviceType
            if (this.deviceType == 'wifi') this.macAddress = response.data.device.deviceId
            else if (this.deviceType == Constants.HsbG3DeviceType) this.shortDeviceId = response.data.device.deviceId
            else this.searchLabel = response.data.device.deviceId
          }
          this.$bvModal.show('generator-enrollment-success')
        })
        .catch(error => {
          this.enrollingApparatus = false
          this.enrollDeviceError = error.response.data
          this.showEnrollApparatusError = true
        })
    },
    validateDevice() {
      let appId = null
      if (this.apparatusId) {
        //enrolling a device on an apparatus that has been previously enrolled
        appId = this.apparatusId
      } else if (
        this.newApparatusInfo &&
        this.newApparatusInfo.apparatusId &&
        this.newApparatusInfo.enrollmentSuccess
      ) {
        //enrolling a device on an apparatus that the user just enrolled
        appId = this.newApparatusInfo.apparatusId
      }
      if (appId) {
        this.validatingDevice = true
        this.$store
          .dispatch('ValidateDevice', {
            deviceType: this.deviceType,
            macAddress: this.macAddress,
            searchLabel: this.searchLabel,
            shortDeviceId: this.shortDeviceId,
            apparatusId: appId
          })
          .then(response => {
            this.validatedDeviceInfo = response.data
            this.validatingDevice = false
            if (this.validatedDeviceInfo && this.validatedDeviceInfo.isValid) {
              this.$bvModal.show('enroll-device-step-8')
            }
          })
          .catch(error => {
            this.validatingDevice = false
            if (error && error.response) {
              this.validatedDeviceInfo = error.response.data
              this.validateDeviceErrorMessage = this.validatedDeviceInfo.message
              this.showValidateDeviceError = true
            }
          })
      }
    },
    enrollApparatusDevice() {
      this.enrollingDevice = true
      if (!this.validatedDeviceInfo) {
        this.enrollDeviceErrorMessage = this.$t('xInsufficientDeviceInformation')
        this.showEnrollDeviceError = true
        this.enrollingDevice = false
        return
      }
      let appId = null
      if (this.apparatusId) {
        //enrolling a device on an apparatus that has been previously enrolled
        appId = this.apparatusId
      } else if (
        this.newApparatusInfo &&
        this.newApparatusInfo.apparatusId &&
        this.newApparatusInfo.enrollmentSuccess
      ) {
        //enrolling a device on an apparatus that the user just enrolled
        appId = this.newApparatusInfo.apparatusId
      }
      if (appId) {
        this.$store
          .dispatch('AddApparatusDevice', {
            apparatusId: appId,
            deviceType: this.validatedDeviceInfo.deviceType,
            deviceId: this.validatedDeviceInfo.deviceId
          })
          .then(response => {
            this.enrollingDevice = false
            this.$bvModal.show('device-enrollment-success-step-9')
          })
          .catch(error => {
            this.enrollingDevice = false
            this.enrollDeviceErrorMessage = this.$t('xFailedToAddDevice')
            this.showEnrollDeviceError = true
          })
      }
    },
    deviceInfoEntered() {
      if (this.deviceType == 'wifi') {
        if (this.macAddress) return false
        else return true
      } else if (this.deviceType == Constants.HsbG3DeviceType) {
        if (this.shortDeviceId && this.shortDeviceId.length == 9) return false
        else return true
      } else {
        if (this.searchLabel && this.searchLabel.length == 10) return false
        else return true
      }
    },
    selectedDeviceType(type) {
      if (this.deviceType && type == this.deviceType) {
        return 'selectedType'
      } else return
    },
    setDeviceType(type) {
      this.deviceType = type
    },
    addSnToRegistrationList() {
      this.sns.push(this.snToAdd)
      this.snToAdd = null
    },
    registerTransferSwitches() {
      this.registeringSerialNumbers = true
      if (this.snToAdd && this.snToAdd.length >= 7) this.sns.push(this.snToAdd)
      this.$store
        .dispatch('registerSerialNumbers', {
          organizationId: this.accountData.organizationId,
          serialNumbers: this.sns,
          apparatusAddress: this.generatorAddress
        })
        .then(response => {
          this.$bvModal.show('transfer-serial-number-registration-success')
        })
        .catch(error => {
          this.transferSwitchFailureMessage = error.response.data
          this.$bvModal.show('transfer-serial-number-registration-failure')
        })
        .finally(() => {
          this.registeringSerialNumbers = false
        })
    },
    canRegisterSns() {
      if (this.sns.length <= 0 && (!this.snToAdd || this.snToAdd.length < 7)) return false
      else if (this.sns.length > 0 || (this.snToAdd && this.snToAdd.length >= 7)) return true
    },
    setSubscriptionPollingInfo(subscriptionInfo) {
      this.subscriptionPollingInfo = subscriptionInfo
    },
    async closeEnrollmentModals(enrollmentSection = null) {
      if (enrollmentSection) {
        this.$appInsights.trackEvent({
          name: 'apparatus-enrollment-cancel',
          properties: { type: 'generator', section: enrollmentSection }
        })
      }

      // save this before clearing all of the other data below
      const apparatusIdForPolling = this.generatorId

      //reset all info entered in enrollment modals
      this.userEnteredSerialNumber = null
      this.generatorName = null
      this.useAccountAddress = true
      this.consent = null
      this.generatorAddress = null
      this.deviceType = null
      this.macAddress = null
      this.searchLabel = null
      this.shortDeviceId = null
      this.validatingDevice = false
      this.validatedDeviceInfo = null
      this.newApparatusInfo = null
      this.enrollingDevice = false
      this.validatingSN = false
      this.enrollingApparatus = false
      this.validateSNError = null
      this.enrollDeviceError = null
      this.gettingSubscriptions = false
      this.existingSubscriptions = []
      this.annualPaidProduct = null
      this.monthlyPaidProduct = null
      this.freeProduct = null
      this.selectedProductCostPreview = null
      this.selectedProduct = null
      this.planIsSelected = false
      this.gettingPaymentInfo = false
      this.paying = false
      this.existingPaymentInfo = null
      this.useExistingPaymentInfo = true
      this.creditCardNumber = null
      this.expirationMonth = null
      this.expirationYear = null
      this.cvv = null
      this.showCreditCardValidationBanner = false
      this.errorMessages = []
      this.$v && this.$v.reset()
      this.useExistingBillingInformation = true
      this.editingAddress = false
      this.registeringSerialNumbers = false
      this.sns = []
      this.snToAdd = null
      this.transferSwitchFailureMessage = null
      this.yearlyTabActive = true
      this.monthlyTabActive = false
      this.showSavingAccountHolderError = false
      this.showPayForProductError = false
      this.payForProductErrorMessage = null
      this.showSelectSubError = false
      this.showGetSubscriptionInfoError = false
      this.getSubscriptionInfoErrorMessage = null
      this.showSerialNumberValidationError = false
      this.serialNumberValidationMessage = null
      this.showEnrollApparatusError = false
      this.showRegistrationFailedError = false
      this.showValidateDeviceError = false
      this.validateDeviceErrorMessage = null
      this.showEnrollDeviceError = false
      this.enrollDeviceErrorMessage = null
      this.selectedFuelType = null
      this.doesAccountHaveTank = false
      this.apparatusDetails = null
      this.addressFormDisabled = false
      this.isShowingValidationView = false
      this.saving = false
      //close all enrollment modals:
      this.$bvModal.hide('serial-number-step-1')
      this.$bvModal.hide('gen-name-step-2')
      this.$bvModal.hide('generator-fuel-type-step-3')
      this.$bvModal.hide('generator-address-step-4')
      this.$bvModal.hide('confirm-gen-enrollment-step-5')
      this.$bvModal.hide('generator-enrollment-success')
      this.$bvModal.hide('confirm-existing-device')
      this.$bvModal.hide('select-device-type-step-6')
      this.$bvModal.hide('no-wifi-module-modal')
      this.$bvModal.hide('enter-device-info-step-7')
      this.$bvModal.hide('enroll-device-step-8')
      this.$bvModal.hide('device-enrollment-success-step-9')
      this.$bvModal.hide('generator-serial-number-help')
      this.$bvModal.hide('mac-address-help')
      this.$bvModal.hide('tether-serial-number-help')
      this.$bvModal.hide('subscription-modal-hsb')
      this.$bvModal.hide('hsb-limit-reached')
      this.$bvModal.hide('finish-setup-error-modal')
      this.$bvModal.hide('fix-plan-error-modal')
      this.$bvModal.hide('fuel-monitor-limit-reached')
      this.$bvModal.hide('not-allowed-to-enroll-legacy-sub')
      this.$bvModal.hide('fuel-monitors-not-allowed-for-your-region')
      this.closeModal('subscription-success')

      // fetch new data and poll for potential subscription changes (silent so that loading spinner doesn't reappear)
      this.openModal('globalSpinnerModalMedium')
      await this.$store.dispatch('getApparatusesSilent')

      if (!!this.subscriptionPollingInfo && this.accountData.organizationId) {
        await this.pollForSubmissionUpdate(
          'getSubscriptionDetails',
          this.accountData.organizationId,
          subscriptionData => {
            if (subscriptionData && subscriptionData.subscriptions) {
              const subscriptionForApparatus = subscriptionData.subscriptions.find(sub => {
                const hasApparatus = sub.apparatuses?.some(subApp => subApp.apparatusId === apparatusIdForPolling)
                const isProductMatch = sub.product.productId === this.subscriptionPollingInfo.productId

                return hasApparatus && isProductMatch
              })

              return !!subscriptionForApparatus
            }

            return false
          },
          'md'
        )
          .then(pollingResult => {
            if (pollingResult) {
              // clear existing pending changes just in case
              this.$store.dispatch('deletePendingSubscriptionChanges')
            } else {
              const pendingChange = {
                // since this is specific to enrollment, there should only be one id we care about
                // If multiple are subscribed at once, they will still be on the same new (and therefor only) sub
                apparatusIds: [apparatusIdForPolling],
                pendingProductId: this.subscriptionPollingInfo.productId,
                isPendingCancel: false
              }
              this.$store.dispatch('addPendingSubscriptionChanges', pendingChange)
            }
          })
          .catch(() => {
            this.$store.dispatch('displayToast', {
              title: this.$t('xSomethingWentWrong'),
              message: this.$t('xWaitAndTryAgainErrorText'),
              variant: 'danger'
            })
          })
      }

      this.closeModal('globalSpinnerModalMedium')
    },
    // NOTE - this functions are used to track/monitor when the user (somehow) exists the enrollment process before completing the subscription application
    // portion of the enrollment
    // NOTE - for more information about the beforeunload event, please refer to this: https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#usage_notes
    onLeaveBeforeSubscriptionApplication() {
      this.$appInsights.trackEvent({
        name: 'apparatus-enrollment-cancel',
        properties: { type: 'generator', section: this.activeSubscriptionModal }
      })
    },
    trackAnalytics(eventName) {
      this.trackAppInsightsEvent(eventName, {
        page: 'EnrollmentModals',
        organizationId: this.accountProfile.organizationId,
        userId: this.accountProfile.userId
      })
    },
    beginTankEnrollmentFlow() {
      this.trackAnalytics('subscription-success-modal-begin-tank-enrollment-button-clicked')
      this.closeModal('subscription-success')
      this.$bvModal.show('tank-enrollment-step-1')
    },
    learnMoreClicked() {
      window.open(Constants.LimitReachedHelpArticleUrl)
    },
    navToSupportPage() {
      this.$router.push({
        path: `/support`
      })
    },
    navToPlanDetailsPage() {
      // this will need to change to the upgrade page once that's ready
      this.$router.push({ name: 'settingsSubscriptions' })
    }
  },
  watch: {
    modelNumber: function(newVal, oldVal) {
      if (newVal) {
        this.apparatusModelNumber = this.modelNumber
      }
    },
    accountProfile: function(newVal, oldVal) {
      if (newVal) this.accountData = _.cloneDeep(newVal)
    },
    basicGenWithExistingSub: function(newVal, oldVal) {
      this.openSubscriptionModal()
    },
    apparatuses: function(newVal) {
      this.doesAccountHaveTank = newVal?.some(app => app.type === Constants.FuelTankApparatusType)
    },
    useAccountAddress: function(newVal) {
      // Reset the address form disabled state when toggling between account and custom address
      if (newVal) {
        this.addressFormDisabled = false
      }
    }
  },
  async mounted() {
    if (!this.freeFeatures) this.$store.dispatch('getFreeFeatures')
    if (!this.paidFeatures) this.$store.dispatch('getPaidFeatures')
    if (this.modelNumber) {
      this.apparatusModelNumber = this.modelNumber
    }
    this.generatorAddress = this.accountProfile.address
    this.accountData = _.cloneDeep(this.accountProfile)
    if (this.apparatusId) {
      await this.$store.dispatch('getApparatusDetails', this.apparatusId)
      this.generatorName = currentApparatusDetails.name
    }
    if (this.basicGenWithExistingSub) {
      this.openSubscriptionModal()
    }
  }
}
</script>
<style lang="scss">
#subscription-modal-hsb {
  @media (min-width: 576px) {
    .modal-lg {
      max-width: calc(100% - 3rem) !important;
    }
  }
  @media (min-width: 800px) {
    .modal-lg {
      max-width: 800px !important;
    }
  }
}
.subscription-toggles {
  .nav-pills {
    justify-content: center;
  }
  .nav-link {
    border: solid 2px var(--blue-700) !important;
    color: var(--carbon-900);
    height: 44px;
    justify-content: center;
    align-items: center;
    display: flex;
  }
  .nav-item:first-of-type .nav-link {
    border-radius: 9999px 0 0 9999px;
  }

  .nav-item:last-of-type .nav-link {
    border-radius: 0 9999px 9999px 0;
  }

  .active i {
    display: inline-block !important;
  }
}
</style>
<style scoped lang="scss">
.card {
  &:hover {
    background-color: var(--carbon-100);
    color: var(--carbon-900);
    border: solid 1px var(--carbon-500) !important;
  }
}
h1 {
  font-weight: normal;
  line-height: 1.15;
}

.card-header {
  align-items: stretch;
  padding: 0;
  border-bottom: 0;
  height: 40px;
  margin-bottom: 10px;
}

.card-title {
  margin-bottom: 0;
}

.subscription-card {
  max-width: 360px;
}
.selected {
  border: 1px solid var(--brand-400);
  background: white;
  box-shadow: 0px 0px 10px var(--brand-400);
}
.device-type-img {
  max-height: 80px;
}
.generator-success-img {
  max-height: 200px;
}
.sn-tag-img {
  max-height: 200px;
}
.selectedType {
  background-color: var(--blue-100);
  color: var(--carbon-900);
  border: solid 1px var(--blue-700) !important;
}
.inProgress {
  color: lightgray;
}
.collapsed > .when-open,
.not-collapsed > .when-closed {
  display: none;
}
</style>
